<template>
  <div class="page">
    <h2>理疗套餐管理</h2>
    <div class="toolbar">
      <select v-model="filters.status">
        <option value="">全部状态</option>
        <option value="DRAFT">草稿</option>
        <option value="PUBLISHED">已发布</option>
        <option value="OFFLINE">已下架</option>
      </select>
      <button :disabled="loading" @click="loadData">查询</button>
      <button :disabled="loading" @click="openCreate">新增套餐</button>
      <router-link to="/dashboard/therapy-bundle-purchases" class="link-btn">套餐订单管理</router-link>
    </div>
    <table>
      <thead>
        <tr>
          <th>ID</th><th>名称</th><th>子项</th><th>原价</th><th>折扣</th><th>折后价</th><th>有效期</th><th>状态</th><th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ item.name }}</td>
          <td>
            <span v-for="(sub, idx) in (item.items || [])" :key="sub.id || idx" class="item-tag">
              {{ sub.activityName }}<template v-if="sub.therapistGrade">({{ sub.therapistGrade }})</template>×{{ sub.quantity }}<template v-if="idx < item.items.length - 1">、</template>
            </span>
          </td>
          <td>¥{{ item.originalAmount }}</td>
          <td>{{ item.discountRate }}%</td>
          <td>¥{{ item.actualAmount }}</td>
          <td>{{ item.validityDays ? item.validityDays + '天' : '不限' }}</td>
          <td>{{ statusMap[item.status] || item.status }}</td>
          <td>
            <button :disabled="loading" @click="openEdit(item)">编辑</button>
            <button :disabled="loading || item.status==='PUBLISHED'" @click="changeStatus(item, 'PUBLISHED')">发布</button>
            <button :disabled="loading || item.status==='OFFLINE'" @click="changeStatus(item, 'OFFLINE')">下架</button>
            <button :disabled="loading || item.status==='PUBLISHED'" @click="remove(item.id)">删除</button>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="pagination">
      <button :disabled="loading || page===0" @click="changePage(page-1)">上一页</button>
      <span>{{ page + 1 }}</span>
      <button :disabled="loading || page+1>=totalPages" @click="changePage(page+1)">下一页</button>
    </div>

    <div v-if="showDialog" class="dialog-mask">
      <div class="dialog dialog-wide">
        <h3>{{ editingId ? '编辑套餐' : '新增套餐' }}</h3>
        <div class="form-group">
          <label>套餐名称</label>
          <input v-model="form.name" type="text" placeholder="例如：气正骨康养套餐" />
        </div>
        <div class="form-group">
          <label>简介</label>
          <input v-model="form.summary" type="text" />
        </div>
        <div class="form-group">
          <label>描述</label>
          <textarea v-model="form.description" rows="3" />
        </div>
        <div class="form-row">
          <div class="form-group form-col">
            <label>折扣率（%）</label>
            <input v-model.number="form.discountRate" type="number" min="1" max="100" step="0.01" />
          </div>
          <div class="form-group form-col">
            <label>有效天数（空=不限）</label>
            <input v-model.number="form.validityDays" type="number" min="1" />
          </div>
          <div class="form-group form-col">
            <label>排序</label>
            <input v-model.number="form.sortOrder" type="number" />
          </div>
        </div>
        <div class="form-group">
          <label class="full-width">
            <span class="field-label">封面图片</span>
            <span class="field-hint">列表与详情展示用，建议清晰横图。</span>
            <div class="image-upload-wrapper">
              <input
                ref="coverImageInput"
                type="file"
                accept="image/*"
                style="display: none"
                @change="e => uploadImage(e, 'coverImageUrl')"
              />
              <div class="image-preview-container">
                <img
                  v-if="form.coverImageUrl"
                  :src="getPreviewUrl(form.coverImageUrl)"
                  alt="封面预览"
                  class="image-preview"
                />
                <div v-else class="image-placeholder">暂无图片</div>
              </div>
              <div class="image-upload-actions">
                <button type="button" class="ghost-button" @click="$refs.coverImageInput.click()">
                  {{ form.coverImageUrl ? '更换图片' : '选择图片' }}
                </button>
                <button v-if="form.coverImageUrl" type="button" class="danger-link" @click.prevent="form.coverImageUrl = ''">清除</button>
                <span v-if="loadingField === 'coverImageUrl'" class="upload-status">上传中...</span>
              </div>
            </div>
          </label>
        </div>

        <div class="items-section">
          <div class="items-header">
            <strong>套餐子项</strong>
            <button type="button" :disabled="loading" @click="addItem">添加项目</button>
          </div>
          <div v-for="(row, idx) in form.items" :key="idx" class="item-row">
            <select v-model.number="row.activityId" @change="onActivityChange(row)">
              <option :value="null">选择理疗项目</option>
              <option v-for="a in activities" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
            <select v-model="row.therapistGrade" :disabled="!gradeOptionsFor(row.activityId).length">
              <option value="">{{ gradeOptionsFor(row.activityId).length ? '选择级别' : '默认' }}</option>
              <option v-for="g in gradeOptionsFor(row.activityId)" :key="g.grade" :value="g.grade">
                {{ g.grade }}（¥{{ gradeUnitPrice(row.activityId, g.grade) }}）
              </option>
            </select>
            <input v-model.number="row.quantity" type="number" min="1" placeholder="数量" />
            <button type="button" :disabled="form.items.length <= 1" @click="removeItem(idx)">删除</button>
          </div>
          <p class="items-hint">有等级价的项目须固定理疗师级别，用户购买后不可更改。</p>
        </div>

        <div class="price-preview">
          原价合计：¥{{ previewOriginal }} · 折后价：¥{{ previewActual }}
        </div>

        <div class="actions">
          <button :disabled="loading" @click="showDialog=false">取消</button>
          <button :disabled="loading" @click="save">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { therapyBundleService } from '@/services/therapyBundleService'
import { therapyActivityService } from '@/services/therapyActivityService'
import { normalizeOssUrlForPresign } from '@/utils/ossUrl'

export default {
  name: 'TherapyBundleManagement',
  data() {
    return {
      list: [],
      activities: [],
      filters: { status: '' },
      page: 0,
      totalPages: 1,
      loading: false,
      showDialog: false,
      editingId: null,
      loadingField: '',
      previewUrls: {},
      statusMap: { DRAFT: '草稿', PUBLISHED: '已发布', OFFLINE: '已下架' },
      form: this.emptyForm()
    }
  },
  computed: {
    previewOriginal() {
      return this.form.items.reduce((sum, row) => {
        const unit = this.unitPriceFor(row)
        const qty = row.quantity || 0
        return sum + unit * qty
      }, 0).toFixed(2)
    },
    previewActual() {
      const rate = this.form.discountRate || 100
      return (Number(this.previewOriginal) * rate / 100).toFixed(2)
    }
  },
  mounted() {
    this.loadActivities()
    this.loadData()
  },
  methods: {
    emptyForm() {
      return {
        name: '',
        summary: '',
        description: '',
        coverImageUrl: '',
        discountRate: 90,
        validityDays: null,
        sortOrder: 0,
        items: [{ activityId: null, therapistGrade: '', quantity: 1, sortOrder: 0 }]
      }
    },
    gradeOptionsFor(activityId) {
      if (!activityId) return []
      const act = this.activities.find(a => a.id === activityId)
      return act?.gradePrices || []
    },
    resolveBaseGrade(activity) {
      const opts = activity?.gradeOptions
      if (Array.isArray(opts) && opts.length) return String(opts[0] || '').trim()
      if (typeof opts === 'string' && opts.trim()) return opts.split(',')[0].trim()
      return ''
    },
    unitPriceFor(row) {
      const act = this.activities.find(a => a.id === row.activityId)
      if (!act) return 0
      const base = Number(act.price) || 0
      const grade = (row.therapistGrade || '').trim()
      if (!grade) return base
      const baseGrade = this.resolveBaseGrade(act)
      if (grade === baseGrade) return base
      const gp = (act.gradePrices || []).find(g => g.grade === grade)
      return base + (Number(gp?.price) || 0)
    },
    gradeUnitPrice(activityId, grade) {
      return this.unitPriceFor({ activityId, therapistGrade: grade })
    },
    onActivityChange(row) {
      row.therapistGrade = ''
    },
    async loadActivities() {
      try {
        const res = await therapyActivityService.fetchList({ status: 'PUBLISHED', size: 200 })
        this.activities = res.data?.content || []
        const offline = await therapyActivityService.fetchList({ status: 'OFFLINE', size: 200 })
        this.activities = [...this.activities, ...(offline.data?.content || [])]
      } catch (e) {
        console.error(e)
      }
    },
    async loadData() {
      this.loading = true
      try {
        const res = await therapyBundleService.fetchList({ ...this.filters, page: this.page, size: 10 })
        this.list = res.data?.content || []
        this.totalPages = res.data?.totalPages || 1
      } catch (e) {
        alert(e?.response?.data?.error || '加载失败')
      } finally {
        this.loading = false
      }
    },
    changePage(p) {
      this.page = p
      this.loadData()
    },
    openCreate() {
      this.editingId = null
      this.form = this.emptyForm()
      this.showDialog = true
    },
    openEdit(item) {
      this.editingId = item.id
      this.form = {
        name: item.name,
        summary: item.summary || '',
        description: item.description || '',
        coverImageUrl: item.coverImageUrl || '',
        discountRate: item.discountRate,
        validityDays: item.validityDays,
        sortOrder: item.sortOrder ?? 0,
        items: (item.items || []).map((sub, idx) => ({
          activityId: sub.activityId,
          therapistGrade: sub.therapistGrade || '',
          quantity: sub.quantity,
          sortOrder: sub.sortOrder ?? idx
        }))
      }
      if (!this.form.items.length) {
        this.form.items = [{ activityId: null, therapistGrade: '', quantity: 1, sortOrder: 0 }]
      }
      this.showDialog = true
    },
    addItem() {
      this.form.items.push({ activityId: null, therapistGrade: '', quantity: 1, sortOrder: this.form.items.length })
    },
    removeItem(idx) {
      this.form.items.splice(idx, 1)
    },
    async save() {
      this.loading = true
      try {
        const payload = { ...this.form, items: this.form.items.map((row, idx) => ({ ...row, sortOrder: idx })) }
        if (this.editingId) {
          await therapyBundleService.update(this.editingId, payload)
        } else {
          await therapyBundleService.create(payload)
        }
        this.showDialog = false
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || e?.message || '保存失败')
      } finally {
        this.loading = false
      }
    },
    async changeStatus(item, status) {
      this.loading = true
      try {
        await therapyBundleService.updateStatus(item.id, status)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '操作失败')
      } finally {
        this.loading = false
      }
    },
    async remove(id) {
      if (!confirm('确定删除该套餐？')) return
      this.loading = true
      try {
        await therapyBundleService.remove(id)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '删除失败')
      } finally {
        this.loading = false
      }
    },
    async uploadImage(event, field) {
      const file = event.target.files[0]
      if (!file) return
      this.loadingField = field
      try {
        const res = await therapyActivityService.uploadImage(file)
        if (res.success) {
          this.form[field] = res.data.url
        }
      } catch (e) {
        alert(e?.response?.data?.message || '上传失败')
      } finally {
        this.loadingField = ''
        event.target.value = ''
      }
    },
    getPreviewUrl(url) {
      if (!url) return ''
      if (url.startsWith('data:')) return url
      const cacheKey = normalizeOssUrlForPresign(url)
      if (this.previewUrls[cacheKey]) return this.previewUrls[cacheKey]

      if (url.includes('aliyuncs.com')) {
        therapyActivityService.getPresignedUrl(url).then(res => {
          const signed = res.data?.url || res.url
          if (signed) {
            this.previewUrls = { ...this.previewUrls, [cacheKey]: signed }
          }
        }).catch(err => {
          console.error('获取预签名URL失败:', err)
          this.previewUrls = { ...this.previewUrls, [cacheKey]: url }
        })
        return ''
      }

      if (url.startsWith('http')) return url
      return ''
    }
  }
}
</script>

<style scoped>
.page { padding: 16px; }
.toolbar { display: flex; gap: 8px; margin-bottom: 12px; align-items: center; flex-wrap: wrap; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 13px; }
.item-tag { font-size: 12px; }
.dialog-mask { position: fixed; inset: 0; background: rgba(0,0,0,.4); display: flex; align-items: center; justify-content: center; z-index: 100; }
.dialog { background: #fff; padding: 20px; border-radius: 8px; max-width: 520px; width: 90%; max-height: 90vh; overflow: auto; }
.dialog-wide { max-width: 720px; }
.form-group { margin-bottom: 12px; display: flex; flex-direction: column; gap: 4px; }
.full-width { width: 100%; }
.field-label { font-weight: 600; }
.field-hint { font-size: 12px; color: #666; }
.image-upload-wrapper { display: flex; flex-direction: column; gap: 0.75rem; }
.image-preview-container {
  width: 100%; min-height: 160px; border: 2px dashed #d1d5db; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; background: #f9fafb; overflow: hidden;
}
.image-preview { max-width: 100%; max-height: 280px; object-fit: contain; display: block; }
.image-placeholder { color: #9ca3af; font-size: 0.9rem; padding: 2rem; }
.image-upload-actions { display: flex; align-items: center; gap: 0.75rem; }
.upload-status { color: #2563eb; font-size: 0.85rem; font-weight: 500; }
.ghost-button {
  background: transparent; border: 1px solid #d1d5db; color: #374151;
  padding: 0.5rem 1rem; border-radius: 6px; cursor: pointer;
}
.danger-link { color: #ef4444; background: none; border: none; cursor: pointer; padding: 0; }
.form-row { display: flex; gap: 12px; }
.form-col { flex: 1; }
.items-section { margin: 16px 0; border-top: 1px solid #eee; padding-top: 12px; }
.items-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.item-row { display: flex; gap: 8px; margin-bottom: 8px; align-items: center; }
.item-row select { flex: 2; min-width: 120px; }
.item-row input { width: 80px; }
.items-hint { margin: 8px 0 0; font-size: 12px; color: #666; }
.price-preview { margin: 12px 0; font-weight: 600; color: #c45a11; }
.actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
.pagination { margin-top: 12px; display: flex; gap: 8px; align-items: center; }
.link-btn { margin-left: auto; text-decoration: none; color: #1976d2; font-size: 14px; }
</style>
